import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Calendar, User, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (_e) {
    return "";
  }
}

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/blog");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success("Post deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="blog-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-black text-2xl">Blog posts</h2>
          <p className="text-sm text-zinc-600 font-medium">The blog area is hidden from the public site until you publish your first post.</p>
        </div>
        <Link to="/staff/blog/new" className="brut-btn-primary" data-testid="new-blog-btn">
          <Plus className="h-4 w-4" /> New post
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#C8102E]" /></div>
      ) : posts.length === 0 ? (
        <div className="brut-card-static p-10 text-center" data-testid="blog-admin-empty">
          <div className="font-display text-2xl font-black mb-2">No posts yet</div>
          <p className="text-zinc-700 font-medium mb-4">Write your first careers update — students will see it as soon as you publish.</p>
          <Link to="/staff/blog/new" className="brut-btn-primary inline-flex">
            <Plus className="h-4 w-4" /> New post
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-admin-grid">
          {posts.map((p) => (
            <div key={p.id} className="brut-card-static p-0 overflow-hidden" data-testid={`blog-admin-card-${p.id}`}>
              {p.cover ? (
                <div className="aspect-video border-b-2 border-black overflow-hidden bg-zinc-100">
                  <img src={`${API}/files/${p.cover.id}`} alt="" className="w-full h-full object-cover object-center" />
                </div>
              ) : (
                <div className="aspect-video border-b-2 border-black bg-[#FEF3C7] flex items-center justify-center">
                  <span className="font-display font-black text-4xl text-[#C8102E]">B</span>
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display font-bold text-lg leading-tight flex-1">{p.title}</h3>
                  {p.is_scheduled && (
                    <span className="brut-tag bg-[#FACC15] text-black inline-flex items-center gap-1" data-testid={`blog-scheduled-badge-${p.id}`}>
                      <Clock className="h-3 w-3" /> Scheduled
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-600 font-bold mb-3">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(p.publish_at || p.published_at)}</span>
                  <span className="flex items-center gap-1"><User className="h-3 w-3" />{p.author_name}</span>
                </div>
                <div className="flex gap-2 pt-3 border-t-2 border-black">
                  <Link to={`/staff/blog/edit/${p.id}`} className="brut-btn-secondary text-xs py-1.5 px-3 flex-1" data-testid={`edit-blog-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Link>
                  <button onClick={() => onDelete(p.id)} className="brut-btn-danger text-xs py-1.5 px-3" data-testid={`delete-blog-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
